import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { useHasAccess } from "@hooks/useHasAccess";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { Icon } from "@iconify/react";
import { getFormattedDate, switchHasRole } from "@utils/CommonUtils";
import {
  Button,
  Checkbox,
  Col,
  Form,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  TableColumnsType,
  Tabs,
  Typography,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { debounce, isBoolean } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper, DataTable } from "~/Components";
import { get, post, put } from "~/Services";
import { getCurrentModule, getModulePath } from "~/Utils/StringConstants";

const initialValue = {
  rejectionReason: "",
};

interface FormItems {
  division: string;
  MAGP_CREATOR: boolean;
  MAGP_REVIEWER: boolean;
}

const defaultValue = {
  division: "",
  MAGP_CREATOR: false,
  MAGP_REVIEWER: false,
} as FormItems;

export const MAGPUserManagement = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { hasRole } = useHasAccess();
  const { openToast } = useNotification();
  const [user] = useLocalStorage("user", null);
  const module = getCurrentModule();

  const [data, setData] = useState<any>([]);
  const [modal, setModal] = useState(false);

  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);

  const [isLoading, setIsLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  const [rejectRecord, setRejectRecord] = useState<any>();
  const [refetch, setRefetch] = useState(false);
  const [page, setpage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderByName, setOrderByName] = useState("updatedOn");
  const [pageStart, setPageStart] = useState<number>(0);
  const [searchString, setSearchString] = useState("");
  const [loading, setLoading] = useState(false);
  const [successLoader, setSuccessLoader] = useState(false);
  const [defUser, setDefUser] = useLocalStorage("user", null);
  const [divisionModal, setDivisionModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>();

  const validationSchema1 = yup.object().shape({
    division: yup.string().strict(true).required("Division is required"),
  });

  const onSubmit = async (values: any, formikHelpers: any) => {
    const roles = [];
    for (const [key, value] of Object.entries(values)) {
      if (value && isBoolean(value)) {
        roles.push(key);
      }
    }
    if (roles.length == 0) {
      message.info("Atleast a single role must be requested for onboarding request");
      return;
    }
    const payload = {
      uniqueId: selectedRecord.uniqueId,
      division: values.division,
      newRoles: roles,
    };
    setLoading(true);
    put(`user/onboarded/update-division-roles`, payload)
      .then((res) => {
        setDefUser({ ...defUser, isRequestedStatus: true, status: "pending" });
        setSuccessLoader(true);
        setLoading(false);
        setRefetch((prevState) => !prevState);
        formikHelpers.resetForm();
        setDivisionModal(false);
      })
      .catch((err) => {
        setLoading(false);
        setDivisionModal(false);
        formikHelpers.resetForm();
        setRefetch((prevState) => !prevState);
      });
  };

  const updateRole = (record: any, role: string, isActive: boolean, isOnboarded: boolean) => {
    if (record.roles.length == 1 && record.roles[0] === role) {
      openToast({
        content: "User with no role is not possible. Please select another role before removing the last one.",
        type: "info",
      });
      return;
    }
    setSwitchLoading(true);
    let payload;
    payload = {
      uniqueId: record?.uniqueId,
      newRoles: isActive ? [...record.roles, role] : record.roles.filter((recordRole: any) => recordRole !== role),
    };
    put(`user/${isOnboarded ? "onboarded" : "onboarding"}/update-roles`, payload)
      .then((res: any) => {
        setRefetch((prevState: boolean) => !prevState);
        setSwitchLoading(false);
      })
      .catch((err) => {
        message.error(err);
        setSwitchLoading(false);
      });
  };

  const updateActiveRole = (record: any, activate: boolean) => {
    setButtonLoading(true);
    const formData = new FormData();
    formData.append("uniqueId", record.uniqueId);
    post(`${activate ? "user/activate" : "user/de-activate"}`, formData)
      .then((res: any) => {
        setButtonLoading(false);
        setRefetch((prevState: boolean) => !prevState);
      })
      .catch((err: any) => {
        setButtonLoading(false);
        message.error(err);
      });
  };

  const approveNewRole = (record: any) => {
    setButtonLoading(true);
    const payload = {
      uniqueId: record.uniqueId,
      approved_roles: [...record.roles],
    };
    post(`user/onboarding/approve`, payload)
      .then((res: any) => {
        setButtonLoading(false);
        setRefetch((prevState: boolean) => !prevState);
      })
      .catch((err: any) => {
        setButtonLoading(false);
        message.error(err);
      });
  };

  const activeUserColumn: TableColumnsType = [
    {
      title: "Users",
      dataIndex: "name",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },

    {
      title: "MAGP Creator",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <Switch
          checked={switchHasRole(record, "MAGP_CREATOR")}
          onChange={(value) => updateRole(record, "MAGP_CREATOR", value, true)}
        />
      ),
    },

    {
      title: "MAGP Reviewer",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <Switch
          checked={switchHasRole(record, "MAGP_REVIEWER")}
          onChange={(value) => updateRole(record, "MAGP_REVIEWER", value, true)}
        />
      ),
    },

    {
      title: "Last Updated By",
      dataIndex: "updatedBy",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: "Last Updated On",
      dataIndex: "updatedOn",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedOn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedOn"
            ? "descend"
            : undefined,
    },
    {
      title: "Action",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Popconfirm
            title={"Are you sure to De-Activate the user?"}
            placement="topLeft"
            onConfirm={() => updateActiveRole(record, false)}
            okText={"Yes"}
            cancelText={"Cancel"}
          >
            <Button danger={true}>Deactivate</Button>
          </Popconfirm>

          {/* {hasRole(["SUPER_ADMIN"]) && (
            <Popconfirm
              title={"Are you sure to change the Division of user?"}
              placement="topLeft"
              onConfirm={() => {
                setSelectedRecord(record);
                setDivisionModal(true);
              }}
              okText={"Yes"}
              cancelText={"Cancel"}
            >
              <Button>Change Division</Button>
            </Popconfirm>
          )} */}
        </div>
      ),
    },
  ];

  const deactiveUserColumn: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "name",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "User Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },

    {
      title: "MAGP Creator",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <Switch
          checked={switchHasRole(record, "MAGP_CREATOR")}
          onChange={(value) => updateRole(record, "MAGP_CREATOR", value, false)}
        />
      ),
    },

    {
      title: "MAGP Reviewer",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <Switch
          checked={switchHasRole(record, "MAGP_REVIEWER")}
          onChange={(value) => updateRole(record, "MAGP_REVIEWER", value, false)}
        />
      ),
    },
    {
      title: "Action",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <Popconfirm
          title={"Are you sure to Re-Activate the user"}
          okText={"Yes"}
          cancelText={"Cancel"}
          onConfirm={() => updateActiveRole(record, true)}
        >
          {" "}
          <Button>Re Activate</Button>
        </Popconfirm>
      ),
    },
  ];

  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getRecords(order, column.dataIndex, searchString, pageStart);
  };

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, searchString.length > 0 ? 0 : pageStart);
  }, [query, refetch, pageStart, searchString]);

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    setIsLoading(true);
    let url = `user/onboarded/get/user-list`;
    if (query.get("activeTab") === "activeUsers") {
      url = `user/onboarded/get/user-list?isActive=true&division=Dedicated&page=${currentPage}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}&application=${getModulePath(module)}`;
    } else if (query.get("activeTab") === "newUsers") {
      url = `user/onboarding/get/user-list?division=Dedicated&status=pending&page=${currentPage}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}`;
    } else if (query.get("activeTab") === "deactivatedUsers") {
      url = `user/onboarded/get/user-list?isActive=false&division=Dedicated&page=${currentPage}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}&application=${getModulePath(module)}`;
    } else if (query.get("activeTab") === "rejectedUsers") {
      url = `user/onboarding/get/user-list?&division=Dedicated&status=rejected&page=${currentPage}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}`;
    }
    get(url)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          setTotalRecords(res.response.data.total);
          let envelopes = res.response.data.records;
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        message.error(err);
        setIsLoading(false);
      });
  };

  const items = [
    {
      label: "Active Users",
      key: "activeUsers",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={activeUserColumn?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder, page ? page : 0);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search by User Name or User Email"}
            hideExport={true}
            inputDisable={isLoading}
            isServerSearchApi={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(page) => {
                const pageSize = 10;
                const startIndex = page - 1;
                setpage(page - 1);
                setPageStart(startIndex);
                getRecords(sortOrder, orderByName, searchString, startIndex);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    // {
    //   label: "New Users",
    //   key: "newUsers",
    //   children: (
    //     <>
    //       <DataTable
    //         columns={newUserColumn?.map((column) => ({
    //           ...column,
    //           onHeaderCell: (column: any) => ({
    //             onClick: () => {
    //               if (column.sorter) {
    //                 handleTableSort(column, sortOrder, page ? page : 0);
    //               }
    //             },
    //           }),
    //         }))}
    //         tableData={data}
    //         className="table-clickable"
    //         searchPlaceholderText={"Search"}
    //         hideExport={true}
    //         isServerSearchApi={true}
    //         onSearchChange={handleSearchChange}
    //         multiSelect={false}
    //         isPagination={true}
    //       />
    //       <div className="pagination">
    //         <Pagination
    //           defaultCurrent={1}
    //           current={page + 1}
    //           total={totalRecords}
    //           onChange={(page) => {
    //             const pageSize = 10;
    //             const startIndex = page - 1;
    //             setpage(page - 1);
    //             setPageStart(startIndex);
    //             getRecords(sortOrder, orderByName, searchString, startIndex);
    //           }}
    //           showSizeChanger={false}
    //         />
    //       </div>
    //     </>
    //   ),
    // },
    {
      label: "Deactivated Users",
      key: "deactivatedUsers",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={deactiveUserColumn?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder, page ? page : 0);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search by User Name or User Email"}
            isServerSearchApi={true}
            hideExport={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(page) => {
                const pageSize = 10;
                const startIndex = page - 1;
                setpage(page - 1);
                setPageStart(startIndex);
                getRecords(sortOrder, orderByName, searchString, startIndex);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    // {
    //   label: "Rejected Users",
    //   key: "rejectedUsers",
    //   children: (
    //     <>
    //       <DataTable
    //         columns={RejectedUserColumn?.map((column) => ({
    //           ...column,
    //           onHeaderCell: (column: any) => ({
    //             onClick: () => {
    //               if (column.sorter) {
    //                 handleTableSort(column, sortOrder, page ? page : 0);
    //               }
    //             },
    //           }),
    //         }))}
    //         tableData={data}
    //         className="table-clickable"
    //         searchPlaceholderText={"Search"}
    //         isServerSearchApi={true}
    //         onSearchChange={handleSearchChange}
    //         hideExport={true}
    //         multiSelect={false}
    //         isPagination={true}
    //       />
    //       <div className="pagination">
    //         <Pagination
    //           defaultCurrent={1}
    //           current={page + 1}
    //           total={totalRecords}
    //           onChange={(page) => {
    //             const pageSize = 10;
    //             const startIndex = page - 1;
    //             setpage(page - 1);
    //             setPageStart(startIndex);
    //             getRecords(sortOrder, orderByName, searchString, startIndex);
    //           }}
    //           showSizeChanger={false}
    //         />
    //       </div>
    //     </>
    //   ),
    // },
  ];

  const validationSchema = yup.object().shape({
    rejectionReason: yup.string().required("Reject Reason is required field"),
  });

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/dedicated/magp/user-management?activeTab=activeUsers`, {
        replace: true,
      });
    }
  }, []);

  return (
    <ContentWrapper
      title={"MAGP User Management"}
      actionItems={
        hasRole(["*"]) && (
          <Button
            type="primary"
            onClick={() => navigate(`/dedicated/magp/user-management/add`)}
          >
            <Space>
              <Icon
                icon="akar-icons:plus"
                inline
              />
              Assign
            </Space>
          </Button>
        )
      }
    >
      {(buttonLoading || switchLoading || isLoading) && (
        <div className="loadingOverlay">
          <Space size="middle">
            <Spin size="large" />
          </Space>
        </div>
      )}
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          setPageStart(0);
          setpage(0);
          navigate(`/dedicated/magp/user-management?activeTab=${activeKey}`);
        }}
      ></Tabs>
      <Formik
        onSubmit={(values, formikHelpers) => {
          setCommentLoading(true);
          const payload = {
            uniqueId: rejectRecord.uniqueId,
            rejectionReason: values.rejectionReason,
          };
          post(`/user/onboarding/reject`, payload)
            .then((res: any) => {
              setCommentLoading(false);
              setModal(false);
              setRefetch((prevState: boolean) => !prevState);
            })
            .catch((err: any) => {
              setCommentLoading(false);
              message.error(err);
            });
        }}
        initialValues={initialValue}
        validationSchema={validationSchema}
      >
        {({ submitForm, resetForm, setFieldValue, touched, errors, isValid, dirty, values }) => (
          <Modal
            title={"Rejection Reason"}
            maskClosable={false}
            open={modal}
            onOk={() => {
              submitForm();
            }}
            okButtonProps={{ disabled: !(isValid && dirty) }}
            onCancel={() => {
              setModal(false);
              resetForm();
            }}
            confirmLoading={commentLoading}
            okText={"Reject"}
            width={600}
            centered
          >
            <Form
              layout="vertical"
              onFinish={submitForm}
            >
              <Field name={"rejectionReason"}>
                {({ field, meta }: FieldProps) => {
                  return (
                    <Form.Item
                      label={"Please comment rejection reason"}
                      validateStatus={meta.touched && meta.error ? "error" : ""}
                    >
                      <TextArea
                        rows={4}
                        maxLength={240}
                        showCount
                        {...field}
                      />
                      {meta.touched && meta.error && (
                        <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                      )}
                    </Form.Item>
                  );
                }}
              </Field>
            </Form>
          </Modal>
        )}
      </Formik>
      <Formik
        initialValues={defaultValue}
        onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers)}
        validationSchema={validationSchema1}
      >
        {(props) => {
          return (
            <Modal
              title={
                <Typography.Title
                  level={4}
                  style={{ marginBottom: 10 }}
                >
                  User Division Change
                </Typography.Title>
              }
              open={divisionModal}
              width={500}
              onCancel={() => {
                props.resetForm();
                setDivisionModal(false);
              }}
              okText={"Submit"}
              footer={null}
            >
              <Form
                layout="vertical"
                onFinish={props.handleSubmit}
              >
                <Row style={{ display: "block", width: "100%" }}>
                  <Col
                    xs={24}
                    md={16}
                  >
                    {
                      <>
                        <Row gutter={16}>
                          <Col
                            md={24}
                            sm={24}
                          >
                            <Form.Item label={"Select Division"}>
                              <Field name={`division`}>
                                {({ field }: FieldProps) => (
                                  <Select
                                    showSearch
                                    value={field.value}
                                    filterOption={(input, option: any) =>
                                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    onChange={(e) => {
                                      props.setFieldValue("division", e);
                                    }}
                                    onBlur={() => props.setFieldTouched("division", true)}
                                    status={(props.touched.division && props.errors.division && "error") || ""}
                                  >
                                    <Select.Option
                                      value={"PCD"}
                                      key={"PCD"}
                                    >
                                      {"PCD"}
                                    </Select.Option>
                                    <Select.Option
                                      value={"COMMERCIAL"}
                                      key={"COMMERCIAL"}
                                    >
                                      {"COMMERCIAL"}
                                    </Select.Option>
                                    <Select.Option
                                      value={"LEGAL"}
                                      key={"LEGAL"}
                                    >
                                      {"LEGAL"}
                                    </Select.Option>
                                  </Select>
                                )}
                              </Field>
                              <Typography.Text type="danger">
                                <ErrorMessage name={`division`} />
                              </Typography.Text>
                            </Form.Item>
                          </Col>
                        </Row>
                        {props.values.division == "PCD" && (
                          <Row gutter={16}>
                            <Col md={24}>
                              <Typography.Title
                                level={5}
                                style={{ marginBottom: 10 }}
                              >
                                Choose the access you want
                              </Typography.Title>
                            </Col>
                            {
                              <>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`AIRPORTS`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>LOA Creator</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`CORPORATE`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>LOA Reviewer</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`ERAMAN`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Contract Creator</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`CONTRACT_REVIEWER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Contract Reviewer</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`PCD_USER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Management Dashboard</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`PCD_ADMIN`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox
                                            key="AIRPORTS"
                                            {...field}
                                          >
                                            PCD Admin
                                          </Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                              </>
                            }
                          </Row>
                        )}
                        {props.values.division == "COMMERCIAL" && (
                          <Row gutter={16}>
                            <Col md={24}>
                              <Typography.Title
                                level={5}
                                style={{ marginBottom: 10 }}
                              >
                                Choose the access you want
                              </Typography.Title>
                            </Col>
                            {
                              <>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`CMU_CREATOR`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>CMU Creator</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`CMU_REVIEWER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>CMU Reviewer</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`LOO_CREATOR`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>LOO Creator</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`LOO_REVIEWER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>LOO Reviewer</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`TA_REVIEWER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>TA Reviewer</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`COMMERCIAL_USER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Management Dashboard</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`COMMERCIAL_ADMIN`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox
                                            key="AIRPORTS"
                                            {...field}
                                          >
                                            Commercial Admin
                                          </Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                              </>
                            }
                          </Row>
                        )}
                        {props.values.division == "LEGAL" && (
                          <Row gutter={16}>
                            <Col md={24}>
                              <Typography.Title
                                level={5}
                                style={{ marginBottom: 10 }}
                              >
                                Choose the access you want
                              </Typography.Title>
                            </Col>
                            {
                              <>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`LEGAL_USER`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Legal User</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={`LEGAL_ADMIN`}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>Legal Admin</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                              </>
                            }
                          </Row>
                        )}
                      </>
                    }
                  </Col>
                </Row>
                <Row>
                  <Col
                    xs={16}
                    md={24}
                    sm={24}
                    style={{ textAlign: "right" }}
                  >
                    <Space>
                      <Button
                        htmlType="submit"
                        type="primary"
                        loading={loading}
                      >
                        Submit
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            </Modal>
          );
        }}
      </Formik>
      <SuccessModal
        redirect={`/dedicated/dedicated/magp/user-management?activeTab=activeUsers`}
        title={"Successfully Requested Access"}
        message={"You have Successfully Requested Access for CLM"}
        type={"success"}
        buttonText={"Go Back"}
        open={successLoader}
        setOpen={setSuccessLoader}
      />
    </ContentWrapper>
  );
};
